<template>
  <div v-if="!auth.ready" class="flex h-full items-center justify-center">
    <AltSpinner size="lg" />
  </div>

  <template v-else>
    <!-- Pages publiques, et pages « pleines » (connecté, mais sans le menu). -->
    <RouterView v-if="isPublic || isPlein" />

    <AltAppLayout
      v-else
      :items="nav"
      brand="its"
      title="Plans d'actions"
      :page-title="pageTitle"
      :active-path="cheminActif"
      :link-component="RouterLink"
    >
      <!--
        PAS DE LOGO DANS LE MENU. Il occupait la première ligne de la barre sur
        chaque écran sans rien apprendre : on sait chez qui l'on travaille. Le
        titre reste, lui — c'est ce qui dit de quel outil il s'agit quand on a
        trois onglets ouverts.

        ⚠ CE SLOT DOIT TOUJOURS RENDRE UN ÉLÉMENT, même replié. Un `v-if` seul
        ne laisse qu'un commentaire, et Vue tient un emplacement qui ne rend que
        des commentaires pour NON FOURNI : il retombe alors sur le contenu par
        défaut d'`AltSidebar`, c'est-à-dire le logo IT Solutions qu'on vient
        d'écarter. Replié, la barre ne fait plus que 64 px : le logo y était
        comprimé en largeur par le flex, et paraissait écrasé — symptôme dont
        personne ne rapproche la cause d'un `v-if` qui a l'air de marcher.

        D'où le `v-else` : replié, le titre reste pour les lecteurs d'écran, la
        barre garde son nom, et le repli par défaut ne se déclenche jamais.
      -->
      <template #sidebar-header="{ collapsed }">
        <span v-if="!collapsed" class="truncate font-semibold text-white">Plans d'actions</span>
        <span v-else class="sr-only">Plans d'actions</span>
      </template>

      <!--
        UN DESSIN PAR ENTRÉE, à la place de la puce ronde.

        `AltSidebar` place une pastille par défaut dans cet emplacement : la
        même pour toutes les lignes, donc sans information. Un pictogramme,
        lui, se reconnaît AVANT d'avoir lu le libellé — et c'est ce qui reste
        visible quand la barre est repliée, où il n'y a plus que lui.
      -->
      <template #sidebar-icon="{ item }">
        <AppIcon :nom="pictoDe(item)" :taille="17" />
      </template>

      <template #topbar-actions>
        <div data-tour="topbar" class="flex items-center gap-3">
          <!-- Recherche globale (projets, actions, tâches, idées) -->
          <SearchBox class="hidden md:block" />
          <!--
            PAS DE BOUTON « Découvrir l'outil » ICI. Il doublait l'entrée
            « Découverte » du menu latéral, qui mène au même écran et amorce le
            bac à sable de la même façon (`DecouvertePage` appelle
            `ensureDiscovery` à son montage). Deux portes pour une seule pièce
            occupaient la barre du haut, là où la place est la plus disputée.
          -->
          <!--
            LA CLOCHE ET L'ENTRÉE DE MENU COMPTENT LA MÊME CHOSE (2026-09-02).
            La cloche affichait « depuis la dernière connexion », le menu « non
            lues » : deux nombres différents pour le même sujet, côte à côte, et
            l'un des deux forcément faux aux yeux de qui les compare.
            C'est « non lues » qui reste — c'est le seul qui BAISSE quand on
            traite, et qui ne remet pas le compteur à zéro à la reconnexion en
            faisant disparaître ce qu'on n'a pas regardé.
          -->
          <RouterLink to="/notifications" class="relative inline-flex" title="Notifications">
            <span aria-hidden="true" class="text-lg">🔔</span>
            <span
              v-if="notifs.counts.nonLues > 0"
              class="absolute -right-2 -top-1 min-w-[1.1rem] rounded-full bg-amaranth px-1 text-center text-[10px] font-semibold leading-4 text-white"
            >
              {{ notifs.counts.nonLues > 99 ? '99+' : notifs.counts.nonLues }}
            </span>
            <span class="sr-only">{{ notifs.counts.nonLues }} notifications non lues</span>
          </RouterLink>
          <!-- Menu profil : photo, thème par défaut, contraste, aide, déconnexion. -->
          <ProfileMenu />
        </div>
      </template>

      <RouterView v-slot="{ Component }">
        <Transition name="fade" mode="out-in">
          <component :is="Component" />
        </Transition>
      </RouterView>
    </AltAppLayout>

    <!-- Visite guidée + bandeau de découverte (au-dessus de tout, hors layout). -->
    <TourOverlay />
    <DiscoveryBanner />
    <!-- Proposition d'installer l'outil en application. Se tait d'elle-même si
         le navigateur n'installe pas, ou si l'on a déjà dit non. -->
    <BandeauInstallation />
  </template>

  <!-- Les confirmations, dans la charte : montées UNE fois, appelées de partout
       par `confirmer()`. Hors du `v-else` : une confirmation peut se poser avant
       même que la session soit établie. -->
  <ConfirmerHote />

  <AltToastContainer position="bottom-right" />
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useRoute, useRouter, RouterLink, RouterView } from 'vue-router';
import { createToastStore } from 'altoneo-charting';
import { canViewActionPlans } from '@its/shared';
import { useAuthStore } from '@/stores/auth';
import { useNotificationsStore } from '@/stores/notifications';
import { useTestsStore } from '@/stores/tests';
import { useDevStore } from '@/stores/dev';
import { useDiscoveryStore } from '@/stores/discovery';
import TourOverlay from '@/components/tour/TourOverlay.vue';
import DiscoveryBanner from '@/components/discovery/DiscoveryBanner.vue';
import ProfileMenu from '@/components/ProfileMenu.vue';
import BandeauInstallation from '@/components/BandeauInstallation.vue';
import SearchBox from '@/components/SearchBox.vue';
import AppIcon from '@/components/AppIcon.vue';
import { cheminActifForce } from '@/composables/useNavContext';
import { surChangement } from '@/lib/realtime';
import { surClicNotification } from '@/lib/push';
import ConfirmerHote from '@/components/ConfirmerHote.vue';

createToastStore();

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const notifs = useNotificationsStore();
const tests = useTestsStore();
const dev = useDevStore();
const discovery = useDiscoveryStore();
// Droit « Visualisation des plans d'actions » (Excel « gestion des droits »).
const canViewM1 = computed(() => canViewActionPlans(auth.roles));

// Restaure le mode découverte dès que l'identité est connue (rebranche le pont
// X-Sandbox et la progression si l'utilisateur était en pleine visite).
watch(
  () => auth.user?.userId,
  (id) => {
    if (id) discovery.hydrate(id);
  },
  { immediate: true },
);

/*
 * LA PASTILLE EN TEMPS RÉEL. Elle se rafraîchissait à chaque navigation, ce qui
 * la laissait fausse tant qu'on restait sur le même écran — précisément le cas
 * de quelqu'un qui travaille. Abonnement au niveau de la coquille : elle est
 * visible partout, elle doit donc écouter partout.
 */
surChangement(['notifications'], () => void notifs.refreshCounts());

/*
 * LE CLIC SUR UNE NOTIFICATION SYSTÈME atterrit ici. Le service worker a repris
 * la fenêtre et nous passe le chemin : on navigue avec le ROUTEUR, sans
 * recharger — recharger perdrait la saisie en cours pour aller lire une
 * notification, ce qui serait un curieux échange.
 */
surClicNotification((lien) => void router.push(lien));

// La pastille suit la connexion et se rafraîchit à chaque navigation.
watch(
  () => [auth.isAuthenticated, route.path] as const,
  ([isAuth]) => {
    if (!isAuth) return;
    void notifs.refreshCounts();
    // Conditionne l'affichage de « Tests et améliorations » : il faut donc la
    // connaître avant de dessiner le menu, pas au montage de la page.
    void tests.fetch();
    // Même raison pour « Développement » : la condition est une FONCTION, et
    // seule l'API sait la résoudre.
    void dev.fetchAccess();
  },
  { immediate: true },
);

const isPublic = computed(() => route.meta.public === true);
/** Une page connectée mais sans le menu : la fenêtre d'édition d'un document. */
const isPlein = computed(() => route.meta.plein === true);
const pageTitle = computed(() => (route.meta.title as string | undefined) ?? '');

/**
 * Le chemin dont le menu se sert pour se surligner. C'est celui de la route,
 * SAUF quand un écran en déclare un autre : une mission s'ouvre sur
 * `/projets/:id` et doit pourtant laisser « Missions client » allumé.
 */
const cheminActif = computed(() => cheminActifForce.value ?? route.path);

/**
 * LE DESSIN DE CHAQUE ENTRÉE, indexé par sa ROUTE et non par son libellé : le
 * libellé se retouche au fil des retours d'usage, le chemin non.
 *
 * Le repli est la pastille pleine d'origine — une entrée nouvelle reste donc
 * lisible même si l'on oublie de lui choisir un dessin.
 */
const PICTOS_NAV: Record<string, NomPicto> = {
  '/': 'maison',
  '/mon-espace': 'personne',
  '/notifications': 'cloche',
  '/projets': 'dossier',
  // La mallette revient aux MISSIONS : c'est le dessin d'une prestation.
  '/missions': 'mallette',
  '/actions': 'cible',
  '/taches': 'liste',
  '/plan-de-charge': 'jauge',
  '/tests': 'eprouvette',
  '/developpement': 'code',
  '/plans': 'planchette',
  '/ideation': 'ampoule',
  // Le portefeuille COPIL récupère le demi-cercle — une vue de synthèse, ce
  // qu'il est. Deux entrées ne peuvent pas partager un dessin : replié, le menu
  // ne montre plus QUE lui.
  '/portefeuille': 'demiCercle',
  '/audit': 'registre',
  '/administration': 'reglages',
  '/aide': 'livre',
  '/decouverte': 'boussole',
};

type NomPicto = InstanceType<typeof AppIcon>['$props']['nom'];

const pictoDe = (item: { to?: string }): NomPicto => PICTOS_NAV[item.to ?? ''] ?? 'cercle';

const nav = computed(() => [
  /*
   * SIX THÈMES, ET NON UNE LISTE (2026-09-01).
   *
   * La barre latérale empilait treize entrées sous deux intertitres : « Gestion
   * de projets » ramassait aussi bien l'accueil personnel que le plan de charge
   * et l'espace du testeur. Un menu qui ne groupe pas oblige à relire toute la
   * liste pour retrouver un écran qu'on ouvre pourtant tous les jours.
   *
   * Le découpage suit CE QU'ON VIENT FAIRE, pas l'ordre historique des modules :
   *
   *   Ma gestion        — ce qui m'est personnel ; c'est là qu'on atterrit ;
   *   Gestion de projets— le travail collectif, du portefeuille à la charge ;
   *   Plans d'actions   — le Module 1, qui a son propre cycle ;
   *   Développement     — ce qu'on livre et ce qu'on teste ;
   *   Direction         — l'arbitrage, réservé ;
   *   Aide              — la documentation et la visite.
   */
  { heading: 'Ma gestion' },
  { to: '/', label: 'Accueil' },
  { to: '/mon-espace', label: 'Mon espace' },
  { to: '/notifications', label: 'Notifications', badge: notifs.counts.nonLues || undefined },

  // Module 2 et Module 3 — le travail collectif (CDC p.4 et p.10-11).
  { heading: 'Gestion de projets' },
  { to: '/projets', label: 'Projets' },
  /*
   * MISSIONS CLIENT (0061), juste après « Projets » : c'est la même matière,
   * séparée par la NATURE de l'engagement. « Tous les projets de développement
   * ne sont pas forcément issus d'un plan d'actions » — et une prestation
   * vendue ne se lit pas comme un travail interne.
   */
  { to: '/missions', label: 'Missions client' },
  { to: '/actions', label: 'Actions' },
  { to: '/taches', label: 'Tâches' },
  // Pas d'entrée « Pense-bête » : elle renvoyait sur « Mon espace », onglet
  // Pense-bête. Deux entrées pour un même écran prêtaient à confusion.
  { to: '/plan-de-charge', label: 'Plan de charge' },

  // Module 1. Masqué pour qui n'a pas le droit de visualisation (Testeur),
  // afin de ne pas proposer un écran que l'API refuse.
  ...(canViewM1.value
    ? [
        { heading: "Plans d'actions" },
        { to: '/plans', label: "Plans d'actions" },
        { to: '/ideation', label: 'Idéation' },
      ]
    : []),

  /*
   * DÉVELOPPEMENT — deux écrans, deux publics, un même sujet : ce qu'on livre.
   *
   * « Tests et améliorations » est l'espace du TESTEUR, ouvert par l'étiquette
   * portée sur un projet (0038), sans condition de rôle. « Développement » est
   * celui de ceux qui livrent — la fonction développeur, seule — et porte
   * depuis 2026-09-01 le pilotage des mises en production.
   *
   * Les pastilles comptent ce qui reste À TRAITER, et non le total : un
   * compteur qui ne redescend jamais cesse d'être lu au bout d'une semaine.
   */
  ...(tests.isTester || dev.canOpen ? [{ heading: 'Développement' }] : []),
  ...(tests.isTester
    ? [{ to: '/tests', label: 'Tests et améliorations', badge: tests.aRetester || undefined }]
    : []),
  ...(dev.canOpen
    ? [{ to: '/developpement', label: 'Développement', badge: dev.ouverts || undefined }]
    : []),

  // « Administration (direction) » — CDC p.12.
  ...(auth.isDirection
    ? [
        { heading: 'Direction' },
        { to: '/portefeuille', label: 'Portefeuille (COPIL)' },
        { to: '/audit', label: "Journal d'audit" },
        { to: '/administration', label: 'Administration' },
      ]
    : []),
  // Visite guidée interactive sur un bac à sable isolé.
  { heading: 'Aide' },
  { to: '/aide', label: 'Aide & guides' },
  { to: '/decouverte', label: 'Découverte' },
]);
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
